// Server function: mint a short-lived Vital sign-in token for the iOS SDK.
//
// The iOS Vital SDK authenticates with a per-user sign-in token (not the
// workspace API key, which must never leave the server). We resolve or create
// the Junction/Vital user for the current Supabase uid, then ask Vital for a
// sign-in token via POST /v2/user/{user_id}/sign_in_token. We also upsert a
// `wearable_connections` row in the same `pending` shape used by the other
// providers so the existing webhook + UI list works unchanged.
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { ensureJunctionUser } from "@/lib/junction.server";

function vitalBaseUrl(): string {
  return process.env.JUNCTION_API_BASE_URL || "https://api.sandbox.tryvital.io";
}

function vitalEnvironment(): "sandbox" | "production" {
  return /sandbox/i.test(vitalBaseUrl()) ? "sandbox" : "production";
}

export const getAppleHealthSignInToken = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const apiKey = process.env.JUNCTION_API_KEY;
    if (!apiKey) throw new Error("JUNCTION_API_KEY is not configured");

    const vitalUserId = await ensureJunctionUser(userId);

    const res = await fetch(
      `${vitalBaseUrl()}/v2/user/${encodeURIComponent(vitalUserId)}/sign_in_token`,
      {
        method: "POST",
        headers: {
          "X-Vital-API-Key": apiKey,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      },
    );
    const text = await res.text();
    if (!res.ok) {
      throw new Error(`[vital] sign_in_token failed (${res.status}): ${text.slice(0, 300)}`);
    }
    const body = text ? JSON.parse(text) : {};
    const token: string | undefined = body.sign_in_token || body.token;
    if (!token) throw new Error("Vital sign-in token response missing token");

    // Mirror the connection-row shape used by Fitbit/Oura so the existing
    // webhook + UI list recognises the Apple Health connection.
    await supabase
      .from("wearable_connections")
      .upsert(
        {
          user_id: userId,
          provider: "apple_health",
          status: "pending",
          junction_user_id: vitalUserId,
        },
        { onConflict: "user_id,provider" },
      );

    return {
      vitalUserId,
      token,
      environment: vitalEnvironment(),
      region: "us" as const,
    };
  });

// Disconnect Apple Health: delete the Vital user (server-side) and mark
// the wearable_connections row disconnected. The native plugin's
// `disconnect()` severs the local HealthKit link separately.
export const revokeAppleHealthAccess = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const apiKey = process.env.JUNCTION_API_KEY;
    if (!apiKey) throw new Error("JUNCTION_API_KEY is not configured");

    const { data: row } = await supabase
      .from("wearable_connections")
      .select("junction_user_id")
      .eq("user_id", userId)
      .eq("provider", "apple_health")
      .maybeSingle();

    const vitalUserId = row?.junction_user_id;
    if (vitalUserId) {
      const res = await fetch(
        `${vitalBaseUrl()}/v2/user/${encodeURIComponent(vitalUserId)}`,
        {
          method: "DELETE",
          headers: {
            "X-Vital-API-Key": apiKey,
            Accept: "application/json",
          },
        },
      );
      // 404 is fine — already deleted upstream.
      if (!res.ok && res.status !== 404) {
        const text = await res.text();
        throw new Error(`[vital] delete user failed (${res.status}): ${text.slice(0, 300)}`);
      }
    }

    await supabase
      .from("wearable_connections")
      .update({ status: "disconnected", junction_user_id: null })
      .eq("user_id", userId)
      .eq("provider", "apple_health");

    return { ok: true as const };
  });

